import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";

type ReasonOption = {
  name: string;
  value: string;
};

type NoProofEmploymentReasonModalProps = {
  open: boolean;
  options: ReasonOption[];
  reason: string;
  otherReason: string;
  onReasonChange: (value: string) => void;
  onOtherReasonChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
};

const MAX_OTHER_REASON_LENGTH = 400;

const isOtherValue = (value?: string | null) =>
  (value || "").toLowerCase() === "other";

const NoProofEmploymentReasonModal = ({
  open,
  options,
  reason,
  otherReason,
  onReasonChange,
  onOtherReasonChange,
  onCancel,
  onConfirm,
}: NoProofEmploymentReasonModalProps) => {
  const requiresOtherReason = isOtherValue(reason);
  const isConfirmDisabled =
    !reason || (requiresOtherReason && !otherReason.trim());

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="no-proof-employment-reason-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="no-proof-employment-reason-title">
        Select a reason
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          A reason is required when you select No.
        </Typography>

        <RadioGroup
          name="no-proof-employment-reason"
          value={reason || ""}
          onChange={(event) => onReasonChange(event.target.value)}
        >
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio />}
              label={option.name}
            />
          ))}
        </RadioGroup>

        {requiresOtherReason && (
          <TextField
            fullWidth
            margin="dense"
            label="Other reason"
            value={otherReason || ""}
            onChange={(event) =>
              onOtherReasonChange(
                String(event.target.value || "").slice(0, MAX_OTHER_REASON_LENGTH)
              )
            }
            inputProps={{ maxLength: MAX_OTHER_REASON_LENGTH }}
            multiline
            minRows={3}
            sx={{
              "& .MuiInputBase-inputMultiline": {
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
              },
            }}
            required
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onConfirm} disabled={isConfirmDisabled} variant="contained">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoProofEmploymentReasonModal;
